/**
 * 按模型配置的 tags 过滤模型 ID（保持注册表声明顺序）。
 * 用于功能入口（如图像顶部工具栏的增强/抠图/扩图/重绘）按 tag 动态接入模型，
 * 避免在 UI 层硬编码模型 ID。
 */
export const getModelIdsByTag = <T extends { tags?: string[] }>(
  models: Record<string, T>,
  tag: string
): string[] =>
  Object.entries(models)
    .filter(([, model]) => Array.isArray(model?.tags) && model.tags.includes(tag))
    .map(([id]) => id);

export const getDefaultModelId = <T extends { default?: boolean | number }>(
  models: Record<string, T>
): string => {
  // default 支持优先级：true = 1，数字越大越优先（如「国际版」标 2、通用兜底标 true）。
  // 传入按用户权限过滤后的模型表时，高优先级模型不可见会自然落到低优先级兜底。
  // 过滤后（订阅/权限/supportsEdit）可能没有任何 default 标记，
  // 此时回退到第一个可用模型，避免在渲染期 throw 导致整棵 React 树崩溃。
  let bestId: string | undefined;
  let bestPriority = 0;
  for (const [id, model] of Object.entries(models)) {
    const d = model?.default;
    if (!d) continue;
    const priority = typeof d === 'number' ? d : 1;
    if (priority > bestPriority) {
      bestId = id;
      bestPriority = priority;
    }
  }
  return bestId ?? Object.keys(models)[0] ?? '';
};

/**
 * 读取用户上次选择的模型 ID（保存在 localStorage），若无则返回 undefined。
 * namespace 用于区分不同模型类型（如 'text' | 'image' | 'speech' | 'video'）。
 * 会校验该模型是否仍存在于传入的 models 字典中，不存在则返回 undefined。
 */
export const getLastSelectedModelId = <T>(
  namespace: string,
  models: Record<string, T>
): string | undefined => {
  try {
    if (typeof window === 'undefined' || !window?.localStorage) return undefined;
    const storageKey = `flowmuse:lastModel:${namespace}`;
    const saved = window.localStorage.getItem(storageKey) || undefined;
    if (saved && Object.prototype.hasOwnProperty.call(models, saved)) return saved;
  } catch {}
  return undefined;
};

/**
 * 记住用户最近一次的模型选择到 localStorage。
 */
export const rememberLastSelectedModelId = (
  namespace: string,
  modelId: string
): void => {
  try {
    if (typeof window === 'undefined' || !window?.localStorage) return;
    const storageKey = `flowmuse:lastModel:${namespace}`;
    window.localStorage.setItem(storageKey, modelId);
  } catch {}
};

/**
 * 读取用户预设的“新建节点默认模型”（保存在 localStorage）。
 * - mode !== 'fixed' 时返回 undefined（表示跟随上次选择）
 * - 会校验该模型是否仍存在于传入的 models 字典中，不存在则返回 undefined
 *
 * 存储键约定（由右侧悬浮栏 UI 写入）：
 * - flowmuse:preset-model:${namespace}:mode  -> 'follow_last' | 'fixed'
 * - flowmuse:preset-model:${namespace}:id    -> modelId（仅 fixed 时有意义）
 */
export const getPresetModelId = <T>(
  namespace: string,
  models: Record<string, T>
): string | undefined => {
  try {
    if (typeof window === 'undefined' || !window?.localStorage) return undefined;
    const modeKey = `flowmuse:preset-model:${namespace}:mode`;
    const idKey = `flowmuse:preset-model:${namespace}:id`;
    const mode = (window.localStorage.getItem(modeKey) || '').trim();
    if (mode !== 'fixed') return undefined;
    const saved = (window.localStorage.getItem(idKey) || '').trim();
    if (saved && Object.prototype.hasOwnProperty.call(models, saved)) return saved;
  } catch {}
  return undefined;
};

/**
 * 读取用户预设的“默认宽高比”（由右侧悬浮栏写入 localStorage）。
 * 存储键：flowmuse:preset-aspect-ratio
 *
 * 返回值示例：'16:9' / '9:16' / '1:1'
 */
/**
 * 读取用户为某个模型设置的"默认参数"（保存在 localStorage）。
 * 存储键：flowmuse:preset-model-params:${modelId}
 */
export const getPresetModelParams = (
  modelId: string
): Record<string, any> | undefined => {
  try {
    if (typeof window === 'undefined' || !window?.localStorage) return undefined;
    const key = `flowmuse:preset-model-params:${modelId}`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null && Object.keys(parsed).length > 0) return parsed;
  } catch {}
  return undefined;
};

/**
 * 保存用户为某个模型设置的"默认参数"到 localStorage。
 * 传入 null 或空对象会删除该条记录。
 */
export const setPresetModelParams = (
  modelId: string,
  params: Record<string, any> | null
): void => {
  try {
    if (typeof window === 'undefined' || !window?.localStorage) return;
    const key = `flowmuse:preset-model-params:${modelId}`;
    if (!params || Object.keys(params).length === 0) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(params));
    }
  } catch {}
};

export const getPresetAspectRatio = (): string | undefined => {
  try {
    if (typeof window === 'undefined' || !window?.localStorage) return undefined;
    const raw = window.localStorage.getItem('flowmuse:preset-aspect-ratio');
    const normalized = String(raw ?? '').trim().replaceAll('：', ':');
    return normalized || undefined;
  } catch {}
  return undefined;
};


